import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Grid,
  CircularProgress,
  Divider,
} from '@mui/material'
import { Person } from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../hooks/redux'
import { useUpdateUserMutation } from '../store/api/usersApi'
import { setUser } from '../store/slices/authSlice'
import { gradientBrand } from '../theme/brand'
import { PageHeader } from '../components/PageHeader'

export default function ProfilePage() {
  const { user } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()
  const [updateUser, { isLoading, error }] = useUpdateUserMutation()
  
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Sync với user data khi user thay đổi
  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '')
      setEmail(user.email || '')
    }
  }, [user])

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Update profile function - matches moduleFunctions.ts
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setErrorMessage(null)

    try {
      const updateData: any = {
        email: email,
        full_name: fullName,
      }

      const updatedUser = await updateUser({
        id: user.id,
        ...updateData,
      } as any).unwrap()

      // Update Redux store với user mới
      dispatch(setUser(updatedUser))
      setMessage('Cập nhật thông tin thành công!')
    } catch (err: any) {
      console.error('Update failed:', err)
      setErrorMessage(err?.data?.detail || err?.message || 'Cập nhật thất bại. Vui lòng thử lại.')
    }
  }

  // Change password function - matches moduleFunctions.ts
  const handleChangePassword = async () => {
    setMessage(null)
    setErrorMessage(null)

    // Validate password match
    if (!password || password !== confirmPassword) {
      setErrorMessage('Mật khẩu xác nhận không khớp')
      return
    }

    // Validate password length
    if (password.length < 6) {
      setErrorMessage('Mật khẩu phải có ít nhất 6 ký tự')
      return
    }

    try {
      const updatedUser = await updateUser({
        id: user.id,
        password: password,
      } as any).unwrap()

      // Update Redux store với user mới
      dispatch(setUser(updatedUser))
      setMessage('Đổi mật khẩu thành công!')
      setPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      console.error('Change password failed:', err)
      setErrorMessage(err?.data?.detail || err?.message || 'Đổi mật khẩu thất bại. Vui lòng thử lại.')
    }
  }

  return (
    <Box>
      <PageHeader
        title="Thông tin cá nhân"
        subtitle="Cập nhật họ tên, email và mật khẩu đăng nhập."
        icon={<Person />}
      />

      <Paper
        elevation={0}
        sx={(theme) => ({
          p: 4,
          mt: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 8px 32px ${theme.palette.mode === 'light' ? 'rgba(20, 78, 140, 0.08)' : 'rgba(0,0,0,0.2)'}`,
        })}
      >
        <form onSubmit={handleUpdateProfile}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tên đăng nhập"
                value={user.username}
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'unset',
                    color: 'text.secondary',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vai trò"
                value={
                  user.role === 'secretary' ? 'Ban Thư ký' :
                  user.role === 'judge' ? 'Giám khảo' :
                  user.role === 'student' ? 'Sinh viên' :
                  user.role === 'support' ? 'Hỗ trợ kỹ thuật' : user.role
                }
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'unset',
                    color: 'text.secondary',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Họ và tên"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Nhập họ và tên đầy đủ"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                Đổi mật khẩu
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Để trống nếu không muốn đổi mật khẩu
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mật khẩu mới"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="Tối thiểu 6 ký tự"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Xác nhận mật khẩu"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={password !== '' && password !== confirmPassword}
                helperText={password !== '' && password !== confirmPassword ? 'Mật khẩu không khớp' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              {message && (
                <Alert 
                  severity="success" 
                  sx={{ mb: 2 }} 
                  onClose={() => setMessage(null)}
                >
                  {message}
                </Alert>
              )}
              {errorMessage && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2 }} 
                  onClose={() => setErrorMessage(null)}
                >
                  {errorMessage}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Cập nhật thất bại. Vui lòng thử lại.
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  size="large"
                  sx={{
                    background: gradientBrand,
                    '&:hover': {
                      background: gradientBrand,
                      filter: 'brightness(1.06)',
                    },
                    minWidth: 150,
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Đang cập nhật...
                    </>
                  ) : (
                    'Cập nhật thông tin'
                  )}
                </Button>
                {password && (
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleChangePassword}
                    disabled={isLoading || !password || password !== confirmPassword}
                    size="large"
                    sx={{
                      minWidth: 150,
                    }}
                  >
                    Đổi mật khẩu
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  )
}
